import { Button, Select, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import AXIOS from "../../../helpers/api";
import { BASE_API } from "../../../helpers/apiUrl";
import { addKeyInArray, selectSearchOption } from "../../../helpers/siteConfig";
import { toast } from "../../../helpers/toast";
import useExcelDownload from "../../../hooks/useExcelDownload";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import {
  getAllCountTowerAsync2,
  getAllTowerAsync,
  getAllTowerAsync2,
  getCountTowerAsync,
} from "../../../store/features/NP/network/networkAPI";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import {
  GetAllTowerData,
  GetAllTowerParams,
  TableData,
} from "../../../types/redux";
import { stringToArray } from "../../../utils/array";
import { AddZero } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import {
  formatDate,
  formatDateNotNew,
  formatTimeNotNew,
  previousDate,
} from "../../../utils/time";
import CustomTable from "../../common/CustomTable";
import Subheader from "../../ui/Subheader";
import ViewLog from "../Network/ViewLog";

type Props = {
  refresh: boolean;
  keyword?: string;
  siteCode?: string;
  removeRefresh: () => void;
  filter?: {
    regionId?: string;
    areaId?: string;
    territoryId?: string;
    supplierId?: string;
    date?: any;
  };
};

export default function SiteLocator({
  refresh,
  removeRefresh,
  filter,
  keyword = "",
  siteCode = "",
}: Props) {
  const location = useLocation();
  const [status, setStatus] = useState("");
  const dispatch = useAppDispatch();
  const { login } = useAppSelector((state) => state.login);
  const [searchParams] = useSearchParams();
  const [downloadClicked, setDownloadClicked] = useState(false);
  const { excelLoading, downloadExcel, handleExcelLoading } = useExcelDownload(
    "Site-Locator-List-Data"
  );
  const { limit, page, handlePagination, isFirstPage, handleSetFirstPage } =
    useTable(1, 5);
  const { open, toggle, handleOpen } = useModal();
  const { loading, allTowers, towersCount } = useAppSelector(
    (state) => state.network
  );
  const path = stringToArray(location.pathname, "/")?.[0];
  const pgStatus = status ? stringToArray(status, "-") : "";

  const handleRequestChange = (value: string) => {
    setStatus(value);
    handleSetFirstPage(true);
  };

  const columns: ColumnsType<TableData<GetAllTowerData>> = [
    {
      title: "Site ID",
      render: (val: GetAllTowerData) => val?.siteCode,
    },
    {
      title: "Site Name",
      render: (val: GetAllTowerData) => capitalize(val?.towerName),
    },
    // {
    //   title: "Structure Type",
    //   dataIndex: "structureType",
    //   align: "center",
    // },
    // {
    //   title: "Location",
    //   align: "center",
    //   render: (val: NetworkTableColumn) => (
    //     <span
    //       className={`${
    //         val?.location === "Indoor"
    //           ? "text-status-indoor"
    //           : "text-status-outdoor"
    //       }`}
    //     >
    //       {val?.location || "-"}
    //     </span>
    //   ),
    // },
    {
      title: "Region",
      dataIndex: "regionName",
      align: "center",
    },
    {
      title: "Zone",
      dataIndex: "areaName",
      align: "center",
    },
    {
      title: "MP",
      dataIndex: "teritoryName",
      align: "center",
    },
    {
      title: "TT No",
      dataIndex: "numberOfOperation",
      align: "center",
      render: (val) => val || 0,
    },
    {
      title: "Grid fail time",
      dataIndex: "towerTicketLogData",
      align: "center",
      render: (val) => {
        const startTime = val?.pgAlert?.startTime?.time;
        const stopTime = val?.pgAlert?.stopTime?.time;

        // Convert the time format using moment
        const formattedStartTime = startTime
          ? moment(startTime, "hh:mm A").format("h:mm A")
          : "N/A";
        const formattedStopTime = stopTime
          ? moment(stopTime, "hh:mm A").format("h:mm A")
          : "N/A";

        return (
          <>
            <span>{formattedStartTime}</span>
            <span> - </span>
            <span>{formattedStopTime}</span>
          </>
        );
      },
    },

    {
      title: "Status",
      align: "center",
      render: (val: GetAllTowerData) => (
        <span
          className={`status ${
            val?.towerTicketLastData?.status === "pending"
              ? "status-pending"
              : val?.towerTicketLastData?.status === "accepted"
              ? "status-done"
              : val?.towerTicketLastData?.status === "notaccepted"
              ? "status-waiting"
              : val?.towerTicketLastData?.status === "cancel"
              ? "status-danger"
              : null
          }`}
        >
          {val?.towerTicketLastData?.status
            ? capitalize(val?.towerTicketLastData?.status)
            : "N/A"}
        </span>
      ),
    },
    {
      title: "PG Status",
      align: "center",
      render: (val: GetAllTowerData) => (
        <span
          className={`status ${
            val?.towerTicketLogData?.status === "pending"
              ? "status-pending"
              : val?.towerTicketLogData?.missCall
              ? "status-danger"
              : val?.towerTicketLogData?.status === "reached"
              ? "status-waiting"
              : val?.towerTicketLogData?.status === "finished"
              ? "status-done"
              : val?.towerTicketLogData?.status === "start"
              ? "status-info"
              : null
          }`}
        >
          {val?.towerTicketLogData?.missCall
            ? "Missed Call"
            : val?.towerTicketLogData?.status
            ? capitalize(val?.towerTicketLogData?.status)
            : "N/A"}
        </span>
      ),
    },
    {
      title: "Details View",
      align: "center",
      render: (val: GetAllTowerData) => (
        <button
          className={`status border-none ${
            val?.towerTicketLogData?._id
              ? "status-info cursor-pointer"
              : "status-info !text-gray-400"
          }`}
          onClick={() =>
            handleOpen({
              type: "LOG",
              data: val,
            })
          }
          disabled={!val?.towerTicketLogData?._id ? true : false}
        >
          View
        </button>
      ),
    },
  ];

  const networkData = useCallback(() => {
    const params: GetAllTowerParams = {
      limit: limit,
      skip: limit * (page - 1),
    };

    if (login?.role === "region" || login?.role === "finance") {
      params.region = login?.region;
    } else {
      params.region = filter?.regionId;
    }

    params.area = filter?.areaId;
    params.teritory = filter?.territoryId;
    params.suppler = filter?.supplierId;

    const filterData =
      status === "unrespond"
        ? { unrespond: true }
        : status === "missCall"
        ? { filter: ["finished"], missCall: true }
        : { filter: status ? [status] : [] };

    dispatch(
      getAllTowerAsync({
        data: {
          ...filterData,
        },
        others: params,
      })
    );
  }, [
    dispatch,
    filter?.areaId,
    filter?.regionId,
    filter?.territoryId,
    filter?.supplierId,
    limit,
    login?.region,
    login?.role,
    page,
    status,
  ]);

  const networkData2 = useCallback(() => {
    const params: any = {
      limit: limit,
      skip: limit * (page - 1),
    };

    if (login?.role === "region" || login?.role === "finance") {
      params.region = login?.region;
    } else {
      params.region = filter?.regionId;
    }

    params.area = filter?.areaId;
    params.teritory = filter?.territoryId;
    params.suppler = filter?.supplierId;

    if (pgStatus?.[1]) {
      params.pg = pgStatus?.[1];
    }

    dispatch(
      getAllTowerAsync2({
        params,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    limit,
    page,
    login?.region,
    login?.role,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    pgStatus?.[1],
    filter?.regionId,
    filter?.areaId,
    filter?.territoryId,
    filter?.supplierId,
  ]);

  const getCountTowerData = useCallback(() => {
    const params: any = {
      limit: limit,
      skip: limit * (page - 1),
    };

    if (login?.role === "region" || login?.role === "finance") {
      params.region = login?.region;
    } else {
      params.region = filter?.regionId;
    }

    params.area = filter?.areaId;
    params.teritory = filter?.territoryId;
    params.suppler = filter?.supplierId;

    // if (searchParams.get("today") === "true") {
    //   params.today = previousDate(formatDate(new Date(), "YYYY-MM-DD"));
    // } else {
    //   params.startDate = startDate ? previousDate(startDate) : "";
    //   params.endDate = endDate ? previousDate(endDate) : "";
    //   params.today = "";
    // }

    const filterData =
      status === "unrespond"
        ? { unrespond: true }
        : status === "missCall"
        ? { filter: ["finished"], missCall: true }
        : { filter: status ? [status] : [] };

    dispatch(
      getCountTowerAsync({
        data: {
          ...filterData,
        },
        others: params,
      })
    );
  }, [
    dispatch,
    filter?.areaId,
    filter?.regionId,
    filter?.territoryId,
    filter?.supplierId,
    limit,
    login?.region,
    login?.role,
    page,
    status,
  ]);

  const getCountTower2 = useCallback(() => {
    const params: any = {
      limit: limit,
      skip: limit * (page - 1),
    };

    if (login?.role === "region" || login?.role === "finance") {
      params.region = login?.region;
    } else {
      params.region = filter?.regionId;
    }

    params.area = filter?.areaId;
    params.teritory = filter?.territoryId;
    params.suppler = filter?.supplierId;

    if (pgStatus?.[1]) {
      params.pg = pgStatus?.[1];
    }

    dispatch(
      getAllCountTowerAsync2({
        params,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    limit,
    page,
    login?.region,
    login?.role,
    filter?.regionId,
    filter?.areaId,
    filter?.territoryId,
    filter?.supplierId,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    pgStatus?.[1],
  ]);

  useEffect(() => {
    if (pgStatus?.[0] === "pg") {
      networkData2();
      getCountTower2();
    } else {
      networkData();
      getCountTowerData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkData, getCountTowerData, getCountTower2, pgStatus?.[0]]);

  useEffect(() => {
    if (status && isFirstPage) {
      handlePagination(1, 5);
      handleSetFirstPage(false);
    }
  }, [handlePagination, handleSetFirstPage, isFirstPage, status]);

  useEffect(() => {
    if (refresh) {
      setStatus("");
      networkData();
      getCountTowerData();
      handlePagination(1, 5);
      removeRefresh();
    }
  }, [
    dispatch,
    getCountTowerData,
    handlePagination,
    networkData,
    refresh,
    removeRefresh,
  ]);

  // Excel Download Function
  useEffect(() => {
    if (downloadClicked) {
      const handleDownloadExcel = async () => {
        handleExcelLoading(true);
        let excelData = [] as any[];

        const params: any = { limit: 1000, skip: 0 };

        if (
          login?.role === "region" ||
          login?.role === "finance" ||
          login?.role === "vendor"
        ) {
          params.region = login?.region;
        } else {
          params.region = filter?.regionId;
        }

        params.area = filter?.areaId;
        params.teritory = filter?.territoryId;
        params.suppler = filter?.supplierId;

        if (searchParams.get("today") === "true") {
          params.today = previousDate(formatDate(new Date(), "YYYY-MM-DD"));
        } else {
          params.startDate = filter?.date?.from
            ? previousDate(filter?.date?.from)
            : "";
          params.endDate = filter?.date?.to
            ? previousDate(filter?.date?.to)
            : "";
          params.today = "";
        }

        const filterData =
          status === "unrespond"
            ? { unrespond: true }
            : status === "missCall"
            ? { filter: ["finished"], missCall: true }
            : { filter: status ? [status] : [] };

        await (pgStatus?.[0] === "pg"
          ? AXIOS.get(BASE_API.getAllTower2, {
              params,
            })
          : AXIOS.post(
              BASE_API.getAllTower,
              {
                ...filterData,
                towerName: keyword,
                siteCode,
              },
              {
                params,
              }
            )
        )
          .then((res) => {
            const responseData = res?.data?.getData as GetAllTowerData[];

            responseData?.forEach((el) => {
              let data = {} as any;
              const startTime =
                el?.towerTicketLogData?.pgAlert?.startTime?.time;
              const stopTime = el?.towerTicketLogData?.pgAlert?.stopTime?.time;
              const pgConnectTime = el?.towerTicketLogData?.pgStart?.time;
              const ttGenerateTime = el?.towerTicketLastData?.createdAt;

              const formattedStartTime = startTime
                ? moment(startTime, "hh:mm A").format("h:mm A")
                : "";
              const formattedStopTime = stopTime
                ? moment(stopTime, "hh:mm A").format("h:mm A")
                : "";
              const formattedPGConnectTime = pgConnectTime
                ? moment(pgConnectTime, "hh:mm A").format("h:mm A")
                : "-";
              const formattedTTGenerateDate =
                formatDateNotNew(ttGenerateTime) ?? "";

              const formattedTTGenerateTime =
                formatTimeNotNew(ttGenerateTime, "h:mm A") ?? "";

              data["Site Code"] = el?.siteCode ?? "-";
              data["Site Name"] = capitalize(el?.towerName) ?? "-";
              data["Region"] = el?.regionName ?? "-";
              data["Zone"] = el?.areaName ?? "-";
              data["MP"] = el?.teritoryName ?? "-";
              data["TT No"] = el?.numberOfOperation ?? 0;
              data[
                "Grid fail time & date"
              ] = `${formattedStartTime} - ${formattedStopTime}`;
              data["Status"] = el?.towerTicketLastData?.status
                ? capitalize(el?.towerTicketLastData?.status)
                : "-";
              // data["PG Connect Time"] = formattedPGConnectTime;
              // data["Tenant Name"] = el?.contactPersonName ?? "-";
              // data["TT No"] = el?.numberOfOperation ?? 0;
              // data[
              //   "TT Generate Time"
              // ] = `${formattedTTGenerateDate} - ${formattedTTGenerateTime}`;
              // data["TT Status"] = el?.towerTicketLastData?.status
              //   ? capitalize(el?.towerTicketLastData?.status)
              //   : "-";
              data["PG Status"] = el?.towerTicketLogData?.missCall
                ? "Missed Call"
                : el?.towerTicketLogData?.status
                ? capitalize(el?.towerTicketLogData?.status)
                : "-";

              excelData.push(data);
            });
          })
          .catch(() => {
            handleExcelLoading(false);
            toast("error", "Download fail! Please try again");
          });

        downloadExcel(excelData);
      };

      handleDownloadExcel();
      setDownloadClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    downloadClicked,
    filter?.date?.from,
    filter?.date?.to,
    filter?.areaId,
    filter?.regionId,
    filter?.territoryId,
    filter?.supplierId,
    keyword,
    login?.region,
    login?.role,
    pgStatus,
    searchParams,
    siteCode,
    status,
  ]);

  return (
    <section>
      {open.type === "LOG" && (
        <ViewLog
          title="Site Locator Log"
          locatorTitle="Shilmoon West Para - Mymensingh"
          open={open.open}
          data={open?.data}
          handleClose={toggle}
        />
      )}

      <Subheader
        title="Site Locator"
        subTitle={`Total Network: ${AddZero(towersCount)}`}
      >
        <Space size={"middle"}>
          <Button
            loading={excelLoading}
            className="btn-status !bg-status-info-btn"
            icon={<DownloadIcon />}
            onClick={() => setDownloadClicked(true)}
          >
            Export Excel
          </Button>

          <Select
            showSearch
            placeholder="Select"
            optionFilterProp="children"
            onChange={handleRequestChange}
            filterOption={selectSearchOption}
            defaultValue={"All"}
            value={status || "All"}
            allowClear={status ? true : false}
            onClear={() => {
              handlePagination(1, 5);
              handleSetFirstPage(false);
              setStatus("");
            }}
            className="min-w-[10rem]"
            options={[
              {
                value: "pending",
                label: "Did not start",
              },
              {
                value: "start",
                label: "Journey Started",
              },
              {
                value: "reached",
                label: "Reached Location",
              },
              {
                value: "finished",
                label: "Task Solved",
              },
              {
                value: "unrespond",
                label: "Unrespond",
              },
              {
                value: "pg-pending",
                label: "PG yet to run",
              },
              {
                value: "pg-start",
                label: "PG Running",
              },
              {
                value: "pg-stop",
                label: "PG Stopped",
              },
              {
                value: "missCall",
                label: "Missed Call",
              },
            ]}
          />
          <Link to={`/${path}/networks`}>
            <Button type="text" className="secondary-btn">
              See all
            </Button>
          </Link>
        </Space>
      </Subheader>

      <CustomTable<TableData<GetAllTowerData>>
        loading={loading}
        total={towersCount}
        limit={limit}
        page={page}
        handlePagination={handlePagination}
        columns={columns}
        dataList={addKeyInArray(allTowers)}
      />
    </section>
  );
}
